import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Minimize2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HelpdeskWidget = () => {
  const { user, token } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Don't render if not logged in
  if (!user || !token) return null;

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!message.trim() || loading) return;

    const userMsg = message.trim();
    setMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/helpdesk/chat`, {
        message: userMsg,
        context: location.pathname,
        conversation_id: conversationId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setConversationId(res.data.conversation_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Desculpe, ocorreu um erro. Tente novamente.' 
      }]);
    }
    setLoading(false);
  };

  const handleNewConversation = () => {
    setMessages([]);
    setConversationId(null);
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="helpdesk-toggle-btn"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-black flex items-center justify-center shadow-lg hover:bg-primary/90 transition-all hover:scale-105"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat Window */}
      {open && (
        <div 
          className="fixed bottom-6 right-6 z-50 w-[380px] max-h-[520px] bg-card border border-border/60 rounded-lg shadow-2xl flex flex-col overflow-hidden"
          data-testid="helpdesk-chat-window"
        >
          {/* Header */}
          <div className="bg-primary/10 border-b border-border/40 px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <MessageCircle size={16} className="text-primary" />
              </div>
              <div>
                <h3 className="text-sm font-heading">MEYBRA HELPDESK</h3>
                <p className="text-xs text-muted-foreground">AI Assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={handleNewConversation}
                  className="p-1.5 hover:bg-muted/50 rounded text-muted-foreground hover:text-foreground transition-colors"
                  title="New conversation"
                  data-testid="helpdesk-new-conversation"
                >
                  <Minimize2 size={14} />
                </button>
              )}
              <button 
                onClick={() => setOpen(false)}
                className="p-1.5 hover:bg-muted/50 rounded text-muted-foreground hover:text-foreground transition-colors"
                data-testid="helpdesk-close-btn"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px] max-h-[380px]">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <MessageCircle size={32} className="mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">
                  How can I help you?
                </p>
                <div className="mt-4 space-y-2">
                  {[
                    'How do I evaluate an athlete?',
                    'How does coach verification work?',
                    'How do I invite an athlete?'
                  ].map((q, i) => (
                    <button
                      key={i}
                      onClick={() => { setMessage(q); }}
                      className="block w-full text-left px-3 py-2 text-xs bg-muted/30 hover:bg-muted/50 rounded transition-colors text-muted-foreground"
                      data-testid={`helpdesk-suggestion-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                  msg.role === 'user' 
                    ? 'bg-primary/20 text-foreground' 
                    : 'bg-muted/40 text-foreground'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted/40 px-3 py-2 rounded-lg">
                  <Loader2 size={16} className="animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="border-t border-border/40 p-3 flex gap-2 shrink-0">
            <input
              ref={inputRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your question..."
              className="flex-1 bg-muted/30 border border-border/40 rounded px-3 py-2 text-sm outline-none focus:border-primary/50 transition-colors"
              disabled={loading}
              data-testid="helpdesk-input"
            />
            <button
              type="submit"
              disabled={!message.trim() || loading}
              className="bg-primary text-black p-2 rounded hover:bg-primary/90 transition-colors disabled:opacity-50"
              data-testid="helpdesk-send-btn"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default HelpdeskWidget;
